// components/Table/ContentAgentTable.jsx
/**
 * ContentAgentTable - Componente para exibição de dados do Agente de Conteúdo em formato tabular
 * 
 * Este componente renderiza uma tabela simplificada com apenas as colunas:
 * WhatsApp, Nome e Data. Específico para a funcionalidade de Agente de Conteúdo.
 */

import React from 'react';

// Importação dos componentes do Material UI necessários para construção da tabela
import {
  Paper,          // Container com elevação para a tabela
  Table,          // Componente de tabela principal
  TableBody,      // Seção do corpo da tabela
  TableCell,      // Célula individual da tabela
  TableContainer, // Container com rolagem para a tabela
  TableHead,      // Seção do cabeçalho da tabela
  TableRow,       // Linha da tabela
  Typography,     // Componente de texto
  Chip,          // Componente para exibir tags/chips
  Box,           // Container flexível
  Button         // Componente de botão
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { Person, Phone } from '@mui/icons-material';

/**
 * Componente ContentAgentTableRow - Linha individual da tabela
 * 
 * @param {Object} props - Propriedades do componente
 * @param {Object} props.item - Objeto contendo os dados do item
 * @param {Function} props.onWhatsAppClick - Função de callback para clique no WhatsApp
 * @param {number} props.index - Índice da linha
 * @param {string} props.hoveredRow - Identificador da linha com hover
 * @param {Function} props.setHoveredRow - Função para atualizar hover
 * @returns {JSX.Element} Linha da tabela renderizada
 */
function ContentAgentTableRow({ item, onWhatsAppClick, index, hoveredRow, setHoveredRow }) {
  /**
   * Formata a data para exibição no formato brasileiro
   * @param {string} dateString - Data em string
   * @returns {string} Data formatada
   */
  const formatDate = (dateString) => {
    if (!dateString) return '-';

    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch (error) {
      return '-';
    }
  };

  return (
    <TableRow
      sx={{
        cursor: 'default',
        transition: 'all 0.2s ease',
        bgcolor: hoveredRow === `content-${index}`
          ? 'rgba(102, 126, 234, 0.08)'
          : 'transparent',
        transform: hoveredRow === `content-${index}` ? 'scale(1.01)' : 'scale(1)',
        '&:hover': {
          bgcolor: 'rgba(102, 126, 234, 0.05)',
        },
        '& td': {
          borderColor: 'divider',
        },
      }}
      onMouseEnter={() => setHoveredRow(`content-${index}`)}
      onMouseLeave={() => setHoveredRow(null)}
    >
      {/* Coluna Nome - Centralizada */}
      <TableCell align="left">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              width: 32,
              height: 32,
              bgcolor: 'rgba(102, 126, 234, 0.1)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Person sx={{ fontSize: 16, color: '#667eea' }} />
          </Box>
          <Typography sx={{ fontWeight: 'medium', color: 'text.primary' }}>
            {item.nome || item.nomeContato || '-'}
          </Typography>
        </Box>
      </TableCell>

      {/* Coluna WhatsApp - Centralizada e Clicável */}
      <TableCell align="center">
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Box
            component="a"
            href="#"
            onClick={(e) => {
              e.preventDefault();
              onWhatsAppClick?.(item);
            }}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              px: 2,
              py: 1,
              bgcolor: 'rgba(76, 175, 80, 0.1)',
              color: '#4caf50',
              borderRadius: 2,
              textDecoration: 'none',
              fontWeight: 'medium',
              transition: 'all 0.2s ease',
              '&:hover': {
                bgcolor: 'rgba(76, 175, 80, 0.2)',
                boxShadow: '0 2px 8px rgba(76, 175, 80, 0.3)',
                transform: 'scale(1.05)',
              },
            }}
          >
            <Phone sx={{ fontSize: 16 }} />
            {item.whatsappId || item.whatsAppId || item.celular  || '-'}
          </Box>
        </Box>
      </TableCell>

      {/* Coluna Data - Centralizada */}
      <TableCell align="right">
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              px: 2,
              py: 1,
              bgcolor: 'rgba(158, 158, 158, 0.1)',
              color: '#9e9e9e',
              borderRadius: 2,
              fontWeight: 'medium',
            }}
          >
            {formatDate(item.dataCadastro || item.data || item.createdAt || item.dataHoraCadastroAtualizado)}
          </Box>
        </Box>
      </TableCell>
    </TableRow>
  );
}

/**
 * Componente ContentAgentTable
 * 
 * @param {Object} props - Propriedades do componente
 * @param {Array} props.data - Array de objetos contendo os dados a serem exibidos
 * @param {Function} props.onWhatsAppClick - Função de callback executada quando o usuário clica no WhatsApp
 * @param {boolean} props.loading - Indica se os dados estão sendo carregados
 * @returns {JSX.Element} Tabela renderizada com contador e botões de paginação
 */
export default function ContentAgentTable({ data = [], onWhatsAppClick, loading = false }) {
  const theme = useTheme();
  const [hoveredRow, setHoveredRow] = React.useState(null);
  const [visibleUsers, setVisibleUsers] = React.useState(12); // Estado para controlar quantos usuários são visíveis

  return (
    <Paper
      elevation={3}
      sx={{
        mb: 4,
        borderRadius: 3,
        overflow: 'hidden',
        transition: 'all 0.3s ease',
        bgcolor: 'background.paper',
        color: 'text.primary',
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      {/* Header da Tabela */}
      <Box
        sx={{
          background: theme.palette.mode === 'dark'
            ? 'linear-gradient(135deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.08) 100%)'
            : 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
          px: 3,
          py: 2,
          color: theme.palette.text.primary,
          overflowX: 'visible'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Person />
          <Typography variant="h6" sx={{ fontWeight: 'bold', flexGrow: 1 }}>
            Usuários
          </Typography>
          <Chip
            label={`${data.length} ${data.length === 1 ? 'usuário' : 'usuários'}`}
            onClick={() => { }}
            clickable={false}
            sx={{
              bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(30, 24, 24, 0.2)',
              color: 'text.primary',
              fontWeight: 'medium',
              fontSize: '0.875rem',
            }}
          />
        </Box>
      </Box>

      {/* Container da tabela */}
      <TableContainer>
        <Table sx={{ overflow: 'hidden' }}>
          {/* Cabeçalho da tabela */}
          <TableHead>
            {/* Linha de cabeçalho com fundo cinza claro */}
            <TableRow sx={{ backgroundColor: 'transparent' }}>
              {/* Colunas do cabeçalho - Centralizadas e com largura igual */}
              <TableCell align="left" sx={{ width: '33.33%', fontWeight: 'bold', color: 'text.primary', borderColor: 'divider' }}>
                Nome
              </TableCell>
              <TableCell align="center" sx={{ width: '33.33%', fontWeight: 'bold', color: 'text.primary', borderColor: 'divider' }}>
                WhatsAppId
              </TableCell>
              <TableCell align="right" sx={{ width: '33.33%', fontWeight: 'bold', color: 'text.primary', borderColor: 'divider' }}>
                Data de Cadastro
              </TableCell>
            </TableRow>
          </TableHead>

          {/* Corpo da tabela */}
          <TableBody>
            {/* Verifica se há dados para exibir */}
            {data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} sx={{ textAlign: 'center', py: 4 }}>
                  <Typography color="text.secondary">
                    {loading ? 'Carregando...' : 'Nenhum resultado encontrado'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              /* 
                Mapeamento do array de dados para componentes ContentAgentTableRow
                Agora limitado pela quantidade visível (visibleUsers)
                Cada item do array é passado como prop para o componente de linha
                junto com a função de callback para cliques no WhatsApp
              */
              data.slice(0, visibleUsers).map((item, index) => (
                <ContentAgentTableRow
                  key={item.id || index} // Chave única necessária para elementos em listas no React
                  item={item} // Dados do item atual
                  onWhatsAppClick={onWhatsAppClick} // Função de callback para interação
                  index={index}
                  hoveredRow={hoveredRow}
                  setHoveredRow={setHoveredRow}
                />
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Botões de Paginação - só aparece se há mais de 12 itens */}
      {data.length > 12 && (
        <Box sx={{ textAlign: 'center', p: 1.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, flexWrap: 'wrap' }}>
            {/* Botão "Ver mais" - aparece quando há mais itens para mostrar */}
            {visibleUsers < data.length ? (
              <Button
                onClick={() => setVisibleUsers(visibleUsers + 10)}
                variant="outlined"
                size="small"
                sx={{
                  px: 2, py: 0.5,
                  border: '1px solid #667eea',
                  borderRadius: '16px',
                  background: 'rgba(102, 126, 234, 0.08)',
                  color: '#667eea',
                  fontWeight: 500,
                  fontSize: '0.8rem',
                  textTransform: 'none',
                  minWidth: '80px',
                  minHeight: '28px',
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(102, 126, 234, 0.2)',
                    background: 'rgba(102, 126, 234, 0.12)',
                  }
                }}
              >
                Ver mais
              </Button>
            ) : (
              /* Botão "Ver menos" - aparece quando todos os itens estão visíveis */
              <Button
                onClick={() => setVisibleUsers(12)}
                variant="outlined"
                size="small"
                sx={{
                  px: 2, py: 0.5,
                  border: '1px solid #e53935',
                  borderRadius: '16px',
                  background: 'rgba(229, 57, 53, 0.08)',
                  color: '#e53935',
                  fontWeight: 500,
                  fontSize: '0.8rem',
                  textTransform: 'none',
                  minWidth: '80px',
                  minHeight: '28px',
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(229, 57, 53, 0.2)',
                    background: 'rgba(229, 57, 53, 0.12)',
                  }
                }}
              >
                Ver menos
              </Button>
            )}

            {/* Botão "Ver todos" - aparece quando há mais itens para mostrar */}
            {visibleUsers < data.length && (
              <Button
                onClick={() => setVisibleUsers(data.length)}
                variant="outlined"
                size="small"
                sx={{
                  px: 2, py: 0.5,
                  border: '1px solid #43a047',
                  borderRadius: '16px',
                  background: 'rgba(76, 175, 80, 0.08)',
                  color: '#43a047',
                  fontWeight: 500,
                  fontSize: '0.8rem',
                  textTransform: 'none',
                  minWidth: '80px',
                  minHeight: '28px',
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(76, 175, 80, 0.2)',
                    background: 'rgba(76, 175, 80, 0.12)',
                  }
                }}
              >
                Ver todos
              </Button>
            )}
          </Box>
        </Box>
      )}
    </Paper>
  );
}
